import { useState } from "react";
import { documentService, subscriptionService } from "@miplatform/api";
import { DOCUMENT_TYPES, HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import { createUUID } from "@miplatform/utils";

export const useManageMyAccountViewModel = ({ tenantId }) => {
  const [accountInfo, setAccountInfo] = useState({});
  const stateManager = useComponentState();

  const getAccountInfo = () => {
    stateManager.run(async () => {
      const response = await subscriptionService.subscriptions.get(tenantId);

      if (response.status === HTTP_STATUSES.OK) {
        setAccountInfo(response.data);
      }
    });
  };

  const updateAccountInfo = (data) =>
    stateManager.run(async () => {
      subscriptionService.subscriptions.updateTenant({
        id: tenantId,
        data,
        toastMessageConfig: {
          success: {
            text: "Account updated successfully",
          },
        },
      });
    });

  const updateWatermark = async ({ file }) =>
    stateManager.run(async () => {
      documentService.documents.uploadWatermark({
        id: tenantId,
        data: file,
        params: {
          title: file.name,
          externalReferenceId: `subscription-service/watermark/${tenantId}`,
          documentType: DOCUMENT_TYPES.IMAGE,
        },
        contentType: file.type,
        toastMessageConfig: {
          success: {
            text: "Watermark updated successfully",
          },
        },
      });
    });

  const uploadLogo = async ({ file, oldLogo }) => {
    if (oldLogo) {
      await documentService.documents.delete({
        id: oldLogo.id,
        modifiedOn: oldLogo.modifiedOn,
      });
    }
    const uuid = createUUID();

    return stateManager.run(async () => {
      documentService.documents.create({
        id: tenantId,
        data: file,
        params: {
          title: file.name,
          externalReferenceId: `subscription-service/logo/${tenantId}/${uuid}`,
          documentType: DOCUMENT_TYPES.IMAGE,
          category: "LOGO",
          applyWatermark: false,
        },
        filename: `${uuid}.${file.name.replace(" ", "-")}`,
        contentType: file.type,
        toastMessageConfig: {
          success: {
            text: "Logo updated successfully",
          },
        },
      });
    });
  };

  const deleteWatermark = async () =>
    stateManager.run(async () => {
      const response = await documentService.documents.delete({
        id: accountInfo.watermark.id,
        modifiedOn: accountInfo.watermark.modifiedOn,
        toastMessageConfig: {
          success: {
            text: "Watermark was deleted successfully",
          },
        },
      });

      if (response.status === HTTP_STATUSES.OK) {
        setAccountInfo((prev) => ({ ...prev, watermark: null }));
      }
    });

  const deleteLogo = async () =>
    stateManager.run(async () => {
      const response = await documentService.documents.delete({
        id: accountInfo.logo.id,
        modifiedOn: accountInfo.logo.modifiedOn,
        toastMessageConfig: {
          success: {
            text: "Logo was deleted successfully",
          },
        },
      });

      if (response.status === HTTP_STATUSES.OK) {
        setAccountInfo((prev) => ({ ...prev, logo: null }));
      }
    });

  return {
    uploadLogo,
    accountInfo,
    getAccountInfo,
    updateAccountInfo,
    updateWatermark,
    deleteWatermark,
    deleteLogo,
    isLoading: stateManager.isBusy(),
  };
};
